import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Dialog,
  HStack,
  Portal,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import SumsubWebSdk from '@sumsub/websdk-react';
import { useAccess, useKycLevel2 } from 'hooks';
import { useTranslation } from 'localization';
import { UserLevel } from 'types';

import {
  Button,
  ServiceDialogBody,
  ServiceDialogCloseTrigger,
  ServiceDialogHeader,
  serviceDialogShellProps,
} from 'components';

import BorderedBox from '../../../ProfileTab/components/BorderedBox';

const connectorInactive = 'rgba(255,255,255,0.12)';

const StepsLevel: React.FC<{
  levelStep: {
    id: number;
    title: string;
    type: UserLevel | 'CORPORATE';
  }[];
  levelManagement: {
    [level: string]: {
      title: string;
      steps: {
        0: { color: string; icon: any };
        1: { color: string; icon: any };
        2: { color: string; icon: any };
        3: { color: string; icon: any };
      };
      features: { title: string; isActive: boolean; description: string }[];
    };
  };
  role: string;
  // eslint-disable-next-line no-unused-vars
  setSelectedLevel: (value: UserLevel | 'CORPORATE') => void;
}> = ({ levelStep, levelManagement, role, setSelectedLevel }) => {
  const { t } = useTranslation('component.settings.verification');
  const access = useAccess();
  const navigate = useNavigate();

  const {
    data: kycData,
    mutateAsync: upgradeLevel2,
    isPending: isUpgradingLevel2,
  } = useKycLevel2();
  const {
    open: isOpenLevel2,
    onOpen: onOpenLevel2,
    onClose: onCloseLevel2,
  } = useDisclosure();

  const closeKycModalHandler = () => {
    onCloseLevel2();
    navigate(0);
  };

  const r = role || 'LEVEL0';

  return (
    <Box w="100%">
      <BorderedBox title={t('verificationLevels')} w="100%">
        <HStack
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
          gap={{ base: 4, md: 6 }}
          w="100%"
        >
          <HStack
            flexWrap="wrap"
            alignItems="center"
            gap={{ base: 3, lg: 4 }}
            flex="1"
            minW={0}
          >
            {levelStep.map((item) => {
              const step = levelManagement[r].steps[item.id as 0 | 1 | 2 | 3];
              const isPrimary = step.color === 'primary';

              return (
                <HStack key={item.id} alignItems="center" gap={0}>
                  <HStack
                    alignItems="center"
                    gap={2}
                    cursor="pointer"
                    onClick={() => setSelectedLevel(item.type as UserLevel)}
                    py={1}
                    borderRadius="md"
                    _hover={{ bg: 'rgba(255,255,255,0.06)' }}
                  >
                    <Box flexShrink={0} lineHeight={0}>
                      {step.icon}
                    </Box>
                    <Text color={step.color} fontSize="sm" fontWeight="medium">
                      {item.title}
                    </Text>
                  </HStack>
                  {item.id < 3 && (
                    <Box
                      w={8}
                      h="2px"
                      mx={2}
                      bg={isPrimary ? 'primary' : connectorInactive}
                      display={{ base: 'none', lg: 'block' }}
                      flexShrink={0}
                      borderRadius="full"
                    />
                  )}
                </HStack>
              );
            })}
          </HStack>
          {access.kycLevel2Permission && (
            <Button
              variant="ghost"
              title={t('nextLevel')}
              onClick={() => upgradeLevel2([]).then(() => onOpenLevel2())}
              isLoading={isUpgradingLevel2}
              flexShrink={0}
            />
          )}
        </HStack>
      </BorderedBox>
      <Dialog.Root
        closeOnInteractOutside={false}
        open={isOpenLevel2}
        size={{ base: 'full', lg: 'full' }}
        scrollBehavior="inside"
        onOpenChange={(e: { open: boolean }) => {
          if (!e.open) {
            closeKycModalHandler();
          }
        }}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content {...serviceDialogShellProps({ maxW: 'full' })}>
              <ServiceDialogHeader>{t('upgradeToLvl2')}</ServiceDialogHeader>
              <ServiceDialogCloseTrigger />
              <ServiceDialogBody p={0} px={0} py={0} bg="sumsub">
                {kycData && (
                  <SumsubWebSdk
                    accessToken={kycData.token}
                    expirationHandler={() => closeKycModalHandler()}
                  />
                )}
              </ServiceDialogBody>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Box>
  );
};

export default StepsLevel;
